import { ACTION_BAR_HOTKEY_COUNT } from './types/action-bar';
import {
    ANALOG_INPUT_BINDING_IDS,
    GAMEPLAY_INPUT_BINDING_IDS,
    MODIFIER_KEYS,
    UI_INPUT_BINDING_IDS,
    type AnalogInputBinding,
    type DigitalInput,
    type DigitalInputBinding,
    type DigitalInputBindingId,
    type InputBindings,
    type ModifierKey,
    type UiInputBindingId,
} from './types/input-bindings';

const key = (name: string, modifiers?: readonly ModifierKey[]): DigitalInputBinding => ({
    kind: 'digital',
    input: { device: 'keyboard', key: name },
    ...(modifiers ? { modifiers } : {}),
});
const mouse = (button: number, modifiers?: readonly ModifierKey[]): DigitalInputBinding => ({
    kind: 'digital',
    input: { device: 'mouse', button },
    ...(modifiers ? { modifiers } : {}),
});
const gamepad = (button: number): DigitalInputBinding => ({ kind: 'digital', input: { device: 'gamepad', button } });

/** The action-bar binding id for a zero-based action-bar index, or undefined past the end. */
export function toActionBarBindingId(index: number): UiInputBindingId | undefined {
    if (!Number.isInteger(index) || index < 0 || index >= ACTION_BAR_HOTKEY_COUNT) {
        return undefined;
    }
    return `action-bar-${index + 1}` as UiInputBindingId;
}

/**
 * The key each action-bar slot starts on: 1-8, and D for the last slot,
 * where every character starts with Draw (`DRAW_ACTION_BAR_INDEX`). A
 * player can move any slot to any key no other binding uses (see
 * action-bar-keys.ts).
 */
export const DEFAULT_ACTION_BAR_KEYS: readonly string[] = ['1', '2', '3', '4', '5', '6', '7', '8', 'd'];

/**
 * The bindings every player starts with. Mouse layout follows the action-RPG
 * standard (Diablo, Path of Exile, Last Epoch): left click is the contextual
 * action, Shift + left click swings at the cursor, right click is the
 * off-hand. There is no WASD — click-to-move, force-move and the joysticks
 * are the movement inputs.
 */
export const DEFAULT_INPUT_BINDINGS: InputBindings = {
    'primary-action': [mouse(0)],
    'aimed-attack': [mouse(0, ['Shift'])],
    'offhand-attack': [mouse(2)],
    'force-move': [key('a')],
    'dodge': [key(' '), gamepad(0)],
    // Alt alone, as action RPGs show loot names; Alt+key stays the browser's.
    'show-item-names': [key('Alt')],
    'action-bar-1': [key(DEFAULT_ACTION_BAR_KEYS[0]!)],
    'action-bar-2': [key(DEFAULT_ACTION_BAR_KEYS[1]!)],
    'action-bar-3': [key(DEFAULT_ACTION_BAR_KEYS[2]!)],
    'action-bar-4': [key(DEFAULT_ACTION_BAR_KEYS[3]!)],
    'action-bar-5': [key(DEFAULT_ACTION_BAR_KEYS[4]!)],
    'action-bar-6': [key(DEFAULT_ACTION_BAR_KEYS[5]!)],
    'action-bar-7': [key(DEFAULT_ACTION_BAR_KEYS[6]!)],
    'action-bar-8': [key(DEFAULT_ACTION_BAR_KEYS[7]!)],
    'action-bar-9': [key(DEFAULT_ACTION_BAR_KEYS[8]!)],
    'open-menu': [key('Escape'), gamepad(9)],
    'toggle-commission': [key('o')],
    'toggle-map': [key('m')],
    'toggle-inventory': [key('i')],
    'toggle-vessel': [key('v')],
    'show-party': [key('p')],
    // Backtick (tilde's key), where many games open chat or a console.
    'show-chat': [key('`')],
    'toggle-combat-log': [key('l')],
    'open-skills': [key('s')],
    'open-crucibles': [key('c')],
    'open-crafting': [key('b')],
    'open-journey': [key('j')],
    // F8 is where many games put their bug reporter, and Chrome leaves it free.
    'report-bug': [key('F8')],
    // Next to the bug reporter; Chrome leaves F9 free (F7 asks about caret browsing, F10 focuses its menu).
    'take-snapshot': [key('F9')],
    'take-clean-render': [key('F9', ['Shift'])],
    'move': [{ kind: 'analog', input: { device: 'gamepad', stick: 'left' } }],
};

/** Whether two digital inputs are the same physical input. Single-character keys compare case-insensitively. */
export function isSameDigitalInput(a: DigitalInput, b: DigitalInput): boolean {
    if (a.device === 'keyboard' && b.device === 'keyboard') {
        return normalizeKey(a.key) === normalizeKey(b.key);
    }
    if (a.device === 'mouse' && b.device === 'mouse') {
        return a.button === b.button;
    }
    if (a.device === 'gamepad' && b.device === 'gamepad') {
        return a.button === b.button;
    }
    return false;
}

/** `KeyboardEvent.key` in the form bindings compare: single characters lower-cased, named keys unchanged. */
export function normalizeKey(value: string): string {
    return value.length === 1 ? value.toLowerCase() : value;
}

// The backtick is shown as the tilde it shares a key with: a lone backtick is too small to read.
const KEY_LABELS: Readonly<Record<string, string>> = { ' ': 'Space', Escape: 'Esc', '`': '~' };
const MOUSE_LABELS: readonly string[] = ['LMB', 'MMB', 'RMB'];

/** A keyboard key (`KeyboardEvent.key`) as a short label: `Space`, `Esc`, `~`, single characters upper-cased. */
export function formatKeyLabel(key: string): string {
    return KEY_LABELS[key] ?? (key.length === 1 ? key.toUpperCase() : key);
}

/** A short label for a digital binding, e.g. `1`, `Space`, `Shift+LMB`, `Pad 0`. */
export function formatDigitalBindingLabel(binding: DigitalInputBinding): string {
    const { input } = binding;
    const base = input.device === 'keyboard'
        ? formatKeyLabel(input.key)
        : input.device === 'mouse'
            ? MOUSE_LABELS[input.button] ?? `Mouse ${input.button}`
            : `Pad ${input.button}`;
    return [...(binding.modifiers ?? []), base].join('+');
}

function isModifierKey(value: unknown): value is ModifierKey {
    return typeof value === 'string' && (MODIFIER_KEYS as readonly string[]).includes(value);
}

function isNonNegativeInteger(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function parseDigitalInput(value: unknown): DigitalInput | null {
    if (value === null || typeof value !== 'object') {
        return null;
    }
    const candidate = value as Record<string, unknown>;
    if (candidate.device === 'keyboard' && typeof candidate.key === 'string' && candidate.key.length > 0) {
        return { device: 'keyboard', key: candidate.key };
    }
    if ((candidate.device === 'mouse' || candidate.device === 'gamepad') && isNonNegativeInteger(candidate.button)) {
        return { device: candidate.device, button: candidate.button };
    }
    return null;
}

function parseDigitalBinding(value: unknown): DigitalInputBinding | null {
    if (value === null || typeof value !== 'object' || (value as { kind?: unknown }).kind !== 'digital') {
        return null;
    }
    const candidate = value as { input?: unknown; modifiers?: unknown };
    const input = parseDigitalInput(candidate.input);
    if (input === null) {
        return null;
    }
    if (candidate.modifiers === undefined) {
        return { kind: 'digital', input };
    }
    if (!Array.isArray(candidate.modifiers) || !candidate.modifiers.every(isModifierKey)) {
        return null;
    }
    return { kind: 'digital', input, modifiers: [...candidate.modifiers] };
}

/** An analog device players could once bind and no longer can: the on-screen joystick (#333, desktop input only). */
const RETIRED_ANALOG_DEVICE = 'virtual-joystick';

/** Whether a stored analog binding names a device that is gone, so it is dropped rather than failing the whole table. */
function isRetiredAnalogBinding(value: unknown): boolean {
    const input = (value as { input?: { device?: unknown } } | null)?.input;
    return input?.device === RETIRED_ANALOG_DEVICE;
}

function parseAnalogBinding(value: unknown): AnalogInputBinding | null {
    if (value === null || typeof value !== 'object' || (value as { kind?: unknown }).kind !== 'analog') {
        return null;
    }
    const input = (value as { input?: unknown }).input as Record<string, unknown> | null | undefined;
    if (input?.device === 'gamepad' && (input.stick === 'left' || input.stick === 'right')) {
        return { kind: 'analog', input: { device: 'gamepad', stick: input.stick } };
    }
    return null;
}

function parseBindingList<T>(value: unknown, parse: (entry: unknown) => T | null): T[] | null {
    if (!Array.isArray(value)) {
        return null;
    }
    const parsed: T[] = [];
    for (const entry of value) {
        const binding = parse(entry);
        if (binding === null) {
            return null;
        }
        parsed.push(binding);
    }
    return parsed;
}

/**
 * Validates a stored binding table: every binding id present, every binding
 * well formed. Returns undefined for anything else, so a corrupt value falls back
 * to the defaults rather than half-applying.
 */
export function parseInputBindings(value: unknown): InputBindings | undefined {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return undefined;
    }
    const table = value as Record<string, unknown>;
    const result: Record<string, readonly DigitalInputBinding[] | readonly AnalogInputBinding[]> = {};

    const digitalIds: readonly DigitalInputBindingId[] = [...GAMEPLAY_INPUT_BINDING_IDS, ...UI_INPUT_BINDING_IDS];
    for (const id of digitalIds) {
        const bindings = parseBindingList(table[id], parseDigitalBinding);
        if (bindings === null) {
            return undefined;
        }
        result[id] = bindings;
    }
    for (const id of ANALOG_INPUT_BINDING_IDS) {
        const stored = table[id];
        const bindings = parseBindingList(Array.isArray(stored) ? stored.filter((entry) => !isRetiredAnalogBinding(entry)) : stored, parseAnalogBinding);
        if (bindings === null) {
            return undefined;
        }
        result[id] = bindings;
    }
    return result as InputBindings;
}
